<template>
	<div class="faset datepicker__container" ref="top_container">
		<label
			tabindex="0"
			@focus="picker.show()"
			:for="uniqueId"
			class="datepicker__input"
		>
			<span aria-hidden="true" class="icon icon-calendar" />
			<input tabindex="-1" :id="uniqueId" ref="inputNode" type="text" />
		</label>
		<div
			v-if="container === null"
			class="datepicker__renderTo"
			ref="container"
			aria-hidden="true"
		/>
	</div>
</template>

<script>
import Pikaday from "pikaday";
import { UniqueId, dateIsHoliday } from "../../../utilities";
import moment from "moment";

const today = new Date();
const labelID = new UniqueId("datepicker-");

/**
 * Customized Date Picker using Pikaday
 *
 * https://github.com/Pikaday/Pikaday
 *
 * The props below are directly mapped to options from Pikaday but not all of them are represented - only the most relevant ones were changed to match our needs.
 */
export default {
	name: "CustomDatePicker",
	props: {
		/**
		 * Set min date (does not work in iOS native datepicker). Figured it out via some research showing the oldest mutual fund created is from 1924: https://www.investopedia.com/ask/answers/08/oldestmutualfunds.asp
		 */
		minDate: {
			type: Date,
			default: function () {
				return moment("1924-01-01").toDate();
			}
		},

		/**
		 * Set max date with default of one year in the future from today
		 */
		maxDate: {
			type: Date,
			default: function () {
				return today;
			}
		},

		/**
		 * You can set the initial date highlighted on page load. Defaults to today.
		 */
		defaultDate: {
			type: Date,
			default: function () {
				return today;
			}
		},

		/**
		 * Puts the defaultDate into the input
		 */
		setDefaultDate: {
			default: true,
			type: Boolean
		},

		/**
		 * Set the value in YYYY-MM-DD format
		 *
		 * @ignore
		 */
		setValue: Date,

		/**
		 * first day of the week (0: Sunday, 1: Monday, etc)
		 */
		firstDay: {
			default: 0,
			type: Number
		},

		/**
		 *  number of visible calendars (if you want a double-paned calendar)
		 */
		numberOfMonths: {
			default: 1,
			type: Number
		},

		/**
		 *  disallow selection of Saturdays or Sundays
		 */
		disableWeekends: {
			default: false,
			type: Boolean
		},

		/**
		 *  disallow selection of Vanguard's US Holidays
		 */
		disableVgHolidays: {
			default: false,
			type: Boolean
		},

		/**
		 * the default output format for .toString() and field value
		 */
		format: {
			default: "MM/DD/YYYY",
			type: String
		},

		/**
		 * Give the input a title for a11y
		 */
		title: {
			type: String,
			default: "Enter a date in the format MM/DD/YYYY"
		},

		/**
		 * you can set a specific element in the DOM that the calendar gets rendered to. If not set, there's a default one setup in this component (originally the libary was just appending to the bottom of <body> but I didn't want that)
		 *
		 * @ignore
		 */
		container: {
			default: null
		},

		/**
		 * By default, the year dropdown only shows 10 years at a time.  When you select the top/bottom year from the list Pikaday loads the prev/next 10 years into the list.  This to me seems unintuitive because you have no idea that this happens until you happen to come across this.  So I'm setting a new default to show the entire range at once.
		 */
		yearRange: {
			type: [Number, Array],
			default: function () {
				return [this.minDate.getFullYear(), this.maxDate.getFullYear()];
			}
		}
	},
	data() {
		return {
			htmlNode: {},
			currentDate: {},
			uniqueId: labelID.generate(),
			platform: "platform-browser"
		};
	},
	watch: {
		minDate(newMinDate) {
			if (!this.picker) {
				this.$refs.inputNode.setAttribute(
					"min",
					this.formatForNative(this.minDate)
				);
				return;
			}
			this.picker.setMinDate(newMinDate);
		},
		maxDate(newMaxDate) {
			if (!this.picker) {
				this.$refs.inputNode.setAttribute(
					"max",
					this.formatForNative(this.maxDate)
				);
				return;
			}

			this.picker.setMaxDate(newMaxDate);
			if (this.currentDate > newMaxDate) {
				this.picker.setDate(newMaxDate);
			}
		},
		setValue(newValue) {
			if (!this.picker) {
				this.$refs.inputNode.value = this.formatForNative(newValue);
				return;
			}
			this.picker.setDate(newValue);
		}
	},
	methods: {
		setupNative() {
			this.$refs.top_container.classList.add(this.platform);
			this.htmlNode = this.$refs.inputNode;
			this.htmlNode.type = "date";
			this.htmlNode.setAttribute("min", this.formatForNative(this.minDate));
			this.htmlNode.setAttribute("max", this.formatForNative(this.maxDate));
			if (this.setDefaultDate) {
				this.htmlNode.value = this.formatForNative(this.defaultDate);
			}
			const vm = this;
			this.htmlNode.addEventListener("change", function (e) {
				// for consistency with the Pikaday onSelect callback
				// this will emit a Date instance as well
				let dateVal = moment(e.target.value).toDate();
				vm.$emit("onSelect", dateVal);
			});
		},
		/**
		 * @param {Date} d an instance of Date
		 * @returns {string} YYYY-MM-DD formatted date string
		 */
		formatForNative(d) {
			return moment(d).format("YYYY-MM-DD");
		},
		checkDateInput() {
			// src: https://stackoverflow.com/a/10199306
			var input = document.createElement("input");
			input.setAttribute("type", "date");

			var notADateValue = "not-a-date";
			input.setAttribute("value", notADateValue);

			return input.value !== notADateValue;
		},
		/**
		 * @returns {string|boolean} the platform name (android or iphone) but only if
		 * it supports the native date picker. 'false' if no platform is supported.
		 */
		checkMobileOS() {
			const ua = window.navigator.userAgent;
			const iphone = ua.match(/iPhone/);
			const android = ua.match(/android/i);
			const supportsDate = this.checkDateInput();

			if (iphone && supportsDate) {
				return "iphone";
			}

			if (android && supportsDate) {
				return "android";
			}

			return false;
		},
		disableDayFn(date) {
			let disableDate = false;
			if (this.disableVgHolidays && dateIsHoliday(date)) {
				disableDate = true;
			}
			return disableDate;
		}
	},
	mounted() {
		let platformCheck = this.checkMobileOS();
		if (platformCheck) {
			this.platform = "platform-" + platformCheck;
			this.setupNative();
			return;
		}

		this.htmlNode = this.$refs.inputNode;
		const vm = this;
		this.picker = new Pikaday({
			container: this.container || this.$refs.container,
			ariaLabel: this.title,
			field: this.htmlNode,
			format: this.format,
			minDate: this.minDate,
			maxDate: this.maxDate,
			firstDay: this.firstDay,
			defaultDate: this.defaultDate,
			numberOfMonths: this.numberOfMonths,
			setDefaultDate: this.setDefaultDate,
			showDaysInNextAndPreviousMonths: true,
			enableSelectionDaysInNextAndPreviousMonths: true,
			position: "bottom left",
			reposition: false,
			yearRange: this.yearRange,
			disableWeekends: this.disableWeekends,
			disableDayFn: this.disableDayFn,
			keyboardInput: false,

			// in order to change the weekday titles to the single letters I needed
			// to change it here but you then have to pass in everything else as well
			i18n: {
				previousMonth: "", // the text for the left prev month arrow
				nextMonth: "", // the text for the right next month arrow
				months: [
					"January",
					"February",
					"March",
					"April",
					"May",
					"June",
					"July",
					"August",
					"September",
					"October",
					"November",
					"December"
				],
				weekdays: [
					"Sunday",
					"Monday",
					"Tuesday",
					"Wednesday",
					"Thursday",
					"Friday",
					"Saturday"
				],
				weekdaysShort: ["S", "M", "T", "W", "T", "F", "S"]
			},
			onSelect() {
				// callback function for when a date is selected
				this.currentDate = this.getMoment().toDate();
				vm.$emit("onSelect", this.currentDate);
			},
			onOpen(e) {
				// callback function for when the picker becomes visible
				vm.$emit("onOpen", e);
			},
			onClose(e) {
				// callback function for when the picker is hidden
				vm.$emit("onClose", e);
			},
			onDraw(e) {
				// callback function for when the picker draws a new month
				vm.$emit("onDraw", e);
			}
		});
		this.currentDate = this.defaultDate;
		this.$refs.inputNode.onfocus = (e) => e.target.select(); //For accessibility, select date when in focus
	}
};
</script>

<style lang="scss">
// size of the arrows
$arrows: 40px;
// width of the calendar which is also used in calculating the grid
$calendarWidth: 327;

.faset.date-range-container {
	z-index: 3;

	// this is our custom input that triggers the calendar popup
	.datepicker__input {
		display: block;
		position: relative;
		width: 160px;

		@include to-md {
			width: 100%;
		}

		.icon-calendar {
			color: $color--gray;
			font-size: 25px;
			left: 11px;
			position: absolute;
			top: calc(50% - 14px);
		}

		input {
			@include h6;

			// iOS Safari zooms in on text inputs less than 16px, which is annoying
			// so making it 16px on mobile
			@include to-md {
				font-size: 16px;
			}

			appearance: none;
			border: 2px solid $color--lighter-gray;
			border-radius: 0;
			color: $color--gray;
			background-color: $color--white;
			display: block;
			font-family: inherit;
			min-height: 20px;
			line-height: 20px;
			padding: 26px 16px 22px 44px;
			width: 100%;
		}

		// if the left side is focused:
		&:focus,
		&:focus-within {
			z-index: 2;

			input {
				border-color: $color--black;
				color: $color--black;
			}

			.icon-calendar {
				color: $color--black;
			}
		}
	}

	// iPhone specifc overrides of the above because using native
	// input datepicker which alters the input styles slightly
	.platform-iphone .datepicker__input {
		input {
			padding: 19px 0 16px 43px;
		}
	}

	// custom container for the calendar
	.datepicker__container {
		position: relative;
	}

	.datepicker__renderTo {
		max-height: 319px;
		position: absolute;
		z-index: 2;
	}

	/**
	* The rest of the CSS below is from the Pikaday library itself.
	* copy/pasted from: https://github.com/Pikaday/Pikaday/blob/master/css/pikaday.css
	* then tweaked to our styles. Figured this was better than overriding
	*/

	// the wrapper around the whole pop-up.  Technically could contain
	// multiple calendars but we only use one at a time
	.pika-single {
		background: #fff;
		border: 1px solid #ccc;
		border-bottom-color: #bbb;
		border-top: 0;
		color: #333;
		display: block;
		font-family: $font--default;
		margin: 13px 0 0;
		position: relative;

		&.focused {
			outline: auto;
			outline: -webkit-focus-ring-color auto 5px;
		}
	}

	/*
	clear child float (pika-lendar), using the famous micro clearfix hack
	http://nicolasgallagher.com/micro-clearfix-hack/
	*/
	.pika-single::before,
	.pika-single::after {
		content: " ";
		display: table;
	}

	.pika-single::after {
		clear: both;
	}

	.pika-single.is-hidden {
		display: none;
	}

	// when it's showing
	.pika-single.is-bound {
		@include commonBoxShadow;

		// need to use `!important` here because the Pikaday library set an inline
		// display: static that causes an issues where the date range calendar flashes
		// when switching between dates
		position: absolute !important;
	}

	// inner wrapper around a single calendar
	.pika-lendar {
		float: left;
		margin: 0;
		width: $calendarWidth + px;
	}

	// this wraps the month/year selects and the 2 nav arrows
	.pika-title {
		display: flex;
		flex-direction: row;
		margin-bottom: 10px;
		position: auto; // for IE11
		position: initial;
	}

	// this wraps the month and year selects
	.pika-label {
		$fontSize: 15px;
		$padVert: (48 - $fontSize) / 2;

		background-color: #fff;
		border: 1px solid $color--lighter-gray;
		display: inline-block;
		font-size: $fontSize;
		line-height: 1;
		margin: 0;
		overflow: hidden;
		padding: $padVert 3px $padVert 24px;
		position: relative;
		width: 50%;

		// the red arrow of the select
		&::before {
			border-color: $color--red;
			border-style: solid;
			border-width: 3px 3px 0 0;
			box-sizing: inherit;
			content: "";
			display: inline-block;
			height: 10px;
			position: absolute;
			right: 1em;
			top: 35%;
			transform: rotate(133deg);
			vertical-align: text-top;
			width: 10px;
		}
	}

	// the inner "hidden" select
	.pika-title select {
		appearance: none;
		bottom: 0;
		cursor: pointer;
		display: block;
		height: 100%;
		left: 0;
		margin: 0;
		opacity: 0;
		position: absolute;
		right: 0;
		top: 0;
		width: 100%;
	}

	// the arrows the go to next and prev months
	.pika-prev,
	.pika-next {
		@include commonBoxShadow;

		appearance: none;
		background: transparent;
		border: 0;
		border-radius: 50%;
		cursor: pointer;
		height: $arrows;
		outline: none;
		overflow: hidden;
		padding: 0;
		position: absolute;
		top: calc(152px - #{$arrows/2});
		width: $arrows;
		z-index: 2;

		// icon font for the arrow
		&::before {
			@include iconFont;

			background-color: #fff;
			display: block;
			font-size: 14px;
			height: 100%;
			line-height: $arrows;
			text-align: center;
		}
	}

	.pika-prev {
		left: -$arrows/2;

		&::before {
			content: "\e90b";
		}
	}

	.pika-next {
		right: -$arrows/2;

		&::before {
			content: "\e908";
		}
	}

	// the table that holds the weekday names and all of the dates
	.pika-table {
		border: 0;
		border-collapse: collapse;
		border-spacing: 0;
		margin: 0 auto;
		max-width: 95%;
		text-align: center;
		width: 100%;

		tr {
			border: none;
			margin-bottom: 2px;
		}

		tr:hover td {
			background-color: transparent;
		}
	}

	.pika-table th,
	.pika-table td {
		// minus 5% because max-width above is 95%
		$square: ($calendarWidth - ($calendarWidth * 0.05)) / 7;

		color: $color--black;
		font-size: 15px;
		height: $square + px;
		line-height: 1;
		padding: 0;
		position: relative;
		text-align: center;
		width: $square + px;
		min-height: initial;
	}

	.pika-table th {
		height: 30px;

		abbr {
			color: $color--black;
		}
	}

	// each day is a <button> inside a <td>
	.pika-button {
		background: transparent;
		border: 0;
		border-radius: 50%;
		color: $color--black;
		cursor: pointer;
		display: block;
		font: inherit;
		height: 100%;
		margin: 0;
		outline: none;
		padding: 1px 0 0;
		text-align: center;
		width: 100%;
	}

	.is-today .pika-button {
		font-weight: bold;
	}

	.is-selected .pika-button {
		background: $color--turquoise;
		font-weight: bold;
	}

	.is-startrange .pika-button {
		background: $color--mid-gray;
		box-shadow: none;
		color: #fff;
	}

	.is-endrange .pika-button {
		background: $color--mid-gray;
		box-shadow: none;
		color: #fff;
	}

	.is-disabled .pika-button {
		color: #999;
		cursor: default;
		opacity: 0.3;
		pointer-events: none;
	}

	.is-outside-current-month .pika-button {
		opacity: 0.5;
	}

	.is-selection-disabled {
		cursor: default;
		pointer-events: none;
	}

	.pika-button:hover,
	.pika-row.pick-whole-week:hover .pika-button {
		background: $color--light-turquoise;
		border-radius: 50%;
		box-shadow: none;
	}

	/* styling for abbr */
	.pika-table abbr {
		border-bottom: none;
		text-decoration: none;
	}
}
</style>
